import { FC, useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { translate } from 'translations';
import { coreService } from 'network-utils';
import { Icon } from 'learning-panel-components';
import styles from './highlightNewCourses.module.scss';

const HighlightNewCourses: FC<HighlightNewCoursesProps> = props => {
    const { courses } = props;
    const [isHovered, setIsHovered] = useState<number | null>();
    const navigate = useNavigate();

    const newestCourseRef = useRef<HTMLDivElement>(null);
    const rightSideRef = useRef<HTMLDivElement>(null);

    return (
        <div id="highlight-wrapper-page" className={styles['content-wrapper']}>
            <div className={styles.title}>{translate('NewContent')}</div>
            <div className={styles.courses}>
                <div
                    className={styles['newest-course']}
                    onClick={() => navigate(`/app/course?courseUuid=${courses[0].uuid}`)}
                    ref={newestCourseRef}
                    style={{ backgroundImage: `url(${courses[0].iconUrl})` }}
                >
                    {courses[0].completionPercentage > 0 && (
                        <div className={styles['course-completion']}>
                            <div className={styles['completion-text']}>
                                {translate(courses[0].completionPercentage === 100 ? 'Completed' : 'InProgress')}
                            </div>
                            {courses[0].score !== 0 && courses[0].score && <div className={styles.divider}></div>}
                            {courses[0].completionPercentage === 100 && <Icon name="adminCheck" />}
                            {courses[0].score !== 0 && courses[0].score && (
                                <Icon name="devCoin" className={styles['dev-coin']} />
                            )}
                            {courses[0].score !== 0 && courses[0].score && (
                                <div className={styles['score-text']}>{courses[0].score.toLocaleString()}</div>
                            )}
                        </div>
                    )}
                    <div className={styles['course-info']}>
                        <div className={styles['description-scroller']}>
                            <div className={styles.description}>{courses[0].description}</div>
                        </div>
                        <div className={styles['lessons-coins-wrapper']}>
                            <div className={styles['lessons-wrapper']}>
                                <Icon name="lessonIconLearningPanel" className={styles['lesson-icon']} />
                                <div className={styles['lessons-text']}>
                                    {translate('X_Lessons', { number: String(courses[0].numberOfLessons) })}
                                </div>
                            </div>
                            {courses[0].points > 0 && <div className={styles.divider}></div>}
                            {courses[0].points > 0 && (
                                <>
                                    <div className={styles['coins-text']}>{translate('EarnUpTo')}</div>
                                    <div className={styles['icon-wrapper']}>
                                        <Icon name="devCoin" className={styles['dev-coin']} />
                                        <div className={styles['icons-text']}>{courses[0].points}</div>
                                    </div>
                                </>
                            )}
                            <div className={styles.divider}></div>
                            <div className={styles.duration}>
                                {translate('x_Mins', {
                                    x: String(courses[0].numberOfLessons * 6)
                                })}
                            </div>
                        </div>
                    </div>
                    <div className={styles['course-name-wrapper']}>
                        <div className={styles['course-name']}>
                            <Icon name="courseIconLearningPanel" />
                            <div className={styles.text}>{courses[0].name}</div>
                        </div>
                    </div>
                </div>
                <div
                    className={styles['right-courses-wrapper']}
                    style={{ height: newestCourseRef.current ? newestCourseRef.current.offsetHeight : 0 }}
                >
                    {courses.slice(1).map((course, index) => (
                        <div
                            key={index}
                            onMouseEnter={() => setIsHovered(index)}
                            onMouseLeave={() => setIsHovered(null)}
                            className={styles['course-wrapper']}
                            onClick={() => navigate(`/app/course?courseUuid=${course.uuid}`)}
                        >
                            <div
                                className={styles['course-hover']}
                                style={{
                                    width: isHovered === index ? `${rightSideRef.current?.clientWidth}px` : '0'
                                }}
                            >
                                <div className={styles['course-description']}>{course.description}</div>
                                <div className={styles['lessons-wrapper']}>
                                    <Icon name="lessonIconLearningPanel" className={styles['lesson-icon']} />
                                    <div className={styles['lessons-text']}>
                                        {translate('X_Lessons', {
                                            number: String(course.numberOfLessons)
                                        })}
                                    </div>
                                    <div className={styles.divider}></div>
                                    {course.points > 0 && (
                                        <div className={styles['icon-wrapper']}>
                                            <div className={styles['coins-text']}>{translate('EarnUpTo')}</div>
                                            <Icon name="devCoin" className={styles['dev-coin']} />
                                            <div className={styles['coins-text']}>{course.points}</div>
                                        </div>
                                    )}
                                    <div className={styles.divider}></div>
                                    <div className={styles.duration}>
                                        {translate('x_Mins', {
                                            x: String(course.numberOfLessons * 6)
                                        })}
                                    </div>
                                </div>
                            </div>
                            <div
                                className={styles['right-side-column']}
                                style={{
                                    backgroundImage: `url(${course.iconUrl})`
                                }}
                                ref={rightSideRef}
                            >
                                {course.completionPercentage > 0 && (
                                    <div className={styles['course-completion']}>
                                        <div className={styles['completion-text']}>
                                            {translate(
                                                course.completionPercentage === 100 ? 'Completed' : 'InProgress'
                                            )}
                                        </div>
                                        {course.score !== 0 && course.score && <div className={styles.divider}></div>}
                                        {course.completionPercentage === 100 && <Icon name="adminCheck" />}
                                        {course.score !== 0 && course.score && (
                                            <Icon name="devCoin" className={styles['dev-coin']} />
                                        )}
                                        {course.score !== 0 && course.score && (
                                            <div className={styles['score-text']}>{course.score.toLocaleString()}</div>
                                        )}
                                    </div>
                                )}
                                <div className={styles['display-name-wrapper']}>
                                    <Icon name="courseIconLearningPanel" />
                                    <div className={styles.name}>{course.name}</div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default HighlightNewCourses;

interface HighlightNewCoursesProps {
    courses: coreService.CourseHighlight[];
}
